<template>
  <div class="audio-player-container">
    <div class="audio-player" v-if="podcast">
      <h2>Now Playing</h2>
      <div class="podcast-info">
        <div class="podcast-title">{{ podcast.title }}</div>
        <div class="podcast-date">{{ formatDate(podcast.date) }}</div>
      </div>
      <div class="audio-controls">
        <div class="skip-buttons">
          <button @click="skip(-15)" class="skip-button" aria-label="Skip 15 seconds backward">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M1 4v6h6"></path>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
            </svg>
            <span class="skip-text">15</span>
          </button>
          <button @click="skip(15)" class="skip-button" aria-label="Skip 15 seconds forward">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M23 4v6h-6"></path>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
            <span class="skip-text">15</span>
          </button>
        </div>
        <audio 
          ref="audioPlayer" 
          controls 
          @error="handleAudioError"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onLoadedMetadata"
        >
          <source :src="podcastUrl" type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
    </div>
    <div class="audio-player-placeholder" v-else>
      <p>Choisir un podcast</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "AudioPlayer",
  props: {
    podcast: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      errorMessage: "",
      savedTime: null,
      isAudioReady: false,
      pendingState: null,
    };
  },
  computed: {
    podcastUrl() {
      if (!this.podcast) return "";
      return `${process.env.BASE_URL}${this.podcast.url}`;
    },
  },
  methods: {
    handleAudioError(e) {
      console.error("Audio error:", e);
      this.errorMessage = `Error loading audio: ${
        e.target.error ? e.target.error.message : "Unknown error"
      }`;
    },
    formatDate(dateString) {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);
      return `${year}-${month}-${day}`;
    },
    saveState() {
      if (this.podcast && this.$refs.audioPlayer) {
        const state = {
          podcast: this.podcast,
          time: this.$refs.audioPlayer.currentTime
        };
        console.log('Saving state:', state);
        localStorage.setItem('audioPlayerState', JSON.stringify(state));
      }
    },
    loadState() {
      const savedState = localStorage.getItem('audioPlayerState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          console.log('Loading saved state:', state);
          this.pendingState = state;
          
          if (!this.podcast && state.podcast) {
            this.$emit('restore-podcast', state.podcast);
          }
        } catch (e) {
          console.error('Error loading saved state:', e);
        }
      }
    },
    onTimeUpdate() {
      const currentTime = this.$refs.audioPlayer.currentTime;
      if (Math.abs(currentTime - (this.savedTime || 0)) >= 5) {
        this.savedTime = currentTime;
        this.saveState();
      }
    },
    onLoadedMetadata() {
      console.log('Audio metadata loaded');
      this.isAudioReady = true;
      this.applyPendingState();
    },
    applyPendingState() {
      if (this.isAudioReady && this.pendingState && this.$refs.audioPlayer) {
        console.log('Applying pending state');
        
        if (this.podcast && this.pendingState.podcast.url === this.podcast.url) {
          if (typeof this.pendingState.time === 'number' && this.pendingState.time > 0) {
            console.log('Setting playhead to:', this.pendingState.time);
            this.$refs.audioPlayer.currentTime = this.pendingState.time;
            this.savedTime = this.pendingState.time;
          }
        }
        this.pendingState = null;
      }
    },
    setCurrentTime(time) {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.currentTime = time;
      }
    },
    skip(seconds) {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.currentTime += seconds;
      }
    },
  },
  watch: {
    podcast(newPodcast) {
      if (newPodcast) {
        this.isAudioReady = false;
        
        const savedState = localStorage.getItem('audioPlayerState');
        if (savedState) {
          try {
            const state = JSON.parse(savedState);
            this.pendingState = state;
          } catch (e) {
            console.error('Error parsing saved state:', e);
          }
        }
        
        this.$nextTick(() => {
          if (this.$refs.audioPlayer) {
            this.$refs.audioPlayer.load();
          }
        });
      }
    },
  },
  mounted() {
    this.loadState();
  },
  beforeUnmount() {
    if (this.$refs.audioPlayer && this.$refs.audioPlayer.currentTime > 0) {
      this.saveState();
    }
  },
};
</script>

<style scoped>
.audio-player-container {
  background-color: var(--color-3);
  padding: 20px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100px; /* Match the min-height in App.vue */
}

.audio-player,
.audio-player-placeholder {
  width: 100%;
  height: 100%; /* Ensure full height */
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.audio-player-placeholder {
  text-align: center;
  color: var(--color-7);
  font-style: italic;
}

h2 {
  font-size: 1.2em;
  margin-bottom: 10px;
  color: var(--color-7);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.podcast-info {
  margin-bottom: 15px;
}

.podcast-title {
  font-weight: bold;
  font-size: 1.1em;
  color: var(--color-8);
}

.podcast-date {
  font-size: 0.9em;
  color: var(--color-6);
}

.audio-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* margin-top: 15px; */
}

.skip-buttons {
  display: flex;
  gap: 10px;
}

.skip-button {
  background-color: transparent;
  color: var(--color-7);
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  padding: 0;
  position: relative;
}

.skip-button:hover {
  color: var(--color-8);
}

.skip-button svg {
  width: 100%;
  height: 100%;
}

.skip-text {
  position: absolute;
  font-size: 10px;
  font-weight: bold;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

audio {
  flex-grow: 1;
  margin-left: 15px;
  border-radius: 30px;
  background-color: var(--color-4);
}

audio::-webkit-media-controls-panel {
  background-color: var(--color-4);
}

audio::-webkit-media-controls-current-time-display,
audio::-webkit-media-controls-time-remaining-display {
  color: var(--color-8);
}

.error-message {
  color: var(--color-7);
  margin-top: 10px;
  font-weight: bold;
}

@media (max-width: 768px) {
  .audio-player-container {
    padding: 10px;
    min-height: 80px;
  }

  h2 {
    font-size: 1em;
    margin-bottom: 5px;
  }

  .podcast-info {
    margin-bottom: 10px;
  }

  .podcast-title {
    font-size: 1em;
  }

  .podcast-date {
    font-size: 0.8em;
  }

  .audio-controls {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }

  .skip-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    margin-right: 15px;
    gap: 10px;
    width: auto;
  }

  audio {
    flex-grow: 1;
    width: auto;
    margin-left: 0;
  }

  /* Hide time display on portrait mode */
  @media (orientation: portrait) {
    /* audio::-webkit-media-controls-current-time-display, */
    audio::-webkit-media-controls-time-remaining-display {
      display: none !important;
    }

    /* Ensure the timeline takes up the full width */
    audio::-webkit-media-controls-timeline {
      width: 100% !important;
    }
  }
}

/* Remove the landscape-specific media query */
</style>